import {
  type ApiErrorCause,
  type components,
  getRetryDelayMs,
  isReauthInProgress,
  UserFacingError
} from "@appretio/api";
import { useRef } from "react";

/** Attempts per save while it goes unanswered; a hard-down API must not spin forever. */
export const MAX_SAVE_ATTEMPTS = 6;

/**
 * A save function that keeps one `save_id` and a frozen body until the save's
 * outcome is known. Which answers release the id: `classify` below, and
 * specs/architecture/api.md § Save outcomes.
 */
export function useKeyedSave<Input extends object, Output>(
  post: (body: Input & { save_id: string }) => Promise<Output>
): (input: Input) => Promise<Output> {
  const pending = useRef<(Input & { save_id: string }) | null>(null);

  return async function save(input: Input): Promise<Output> {
    const body = {
      ...input,
      save_id: pending.current?.save_id ?? crypto.randomUUID()
    };
    if (
      pending.current &&
      JSON.stringify(body) !== JSON.stringify(pending.current)
    ) {
      throw new UserFacingError(
        "A previous save was not confirmed. Check the data and reload this page before saving different edits."
      );
    }
    const resumed = pending.current !== null;
    const frozen = structuredClone(body);
    pending.current = frozen;
    for (let attempt = 1; ; attempt++) {
      try {
        const output = await post(frozen);
        pending.current = null;
        return output;
      } catch (error) {
        const outcome = classify(error, attempt === 1 && !resumed);
        if (outcome === "answered") {
          pending.current = null;
        } else if (
          outcome === "unanswered" &&
          error instanceof Error &&
          attempt < MAX_SAVE_ATTEMPTS
        ) {
          const delayMs = getRetryDelayMs(attempt, error);
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          continue;
        }
        throw error;
      }
    }
  };
}

/**
 * What a failed save attempt says of the save: `answered` releases the id,
 * `unanswered` resubmits it, `kept` keeps it without asking again.
 */
function classify(
  error: unknown,
  firstAttempt: boolean
): "answered" | "unanswered" | "kept" {
  if (!(error instanceof Error)) {
    return "answered";
  }
  if (error.name === "AbortError" || isReauthInProgress(error)) {
    // The page cancelled it; the server may still apply it.
    return "kept";
  }
  const cause = error.cause as ApiErrorCause | undefined;
  // Mirrors appretio.error.ErrorCode.JOB_UNKNOWN.
  const unknown: components["schemas"]["ErrorCode"] = 10;
  if (cause?.code === unknown) {
    return "kept";
  }
  if (cause?.jobCompleted) {
    return "answered";
  }
  // Rejected before the handler ran: validation, auth or an unknown route on
  // the initial POST. Nothing delivered through a poll lands here: the poll
  // answers the job's own response, with the header, or a lost 503.
  // Kept after any earlier attempt under this id.
  if (
    cause &&
    cause.status >= 400 &&
    cause.status < 500 &&
    cause.status !== 429
  ) {
    return firstAttempt ? "answered" : "kept";
  }
  return "unanswered";
}
